import { useEffect, useState } from "react";
import { Plus, Network, Pencil, Building2, UserPlus, Mail, CheckCircle2, Clock, Trash2, Power, AtSign } from "lucide-react";
import ConfirmAction from "@/components/ConfirmAction";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CsvImportButton from "@/components/CsvImportButton";
import usePolling from "@/hooks/usePolling";

import { CONTRACTOR_ROLES, roleLabel } from "@/constants/roles";
import { VENDOR_TYPES as VENDOR_TYPE_OPTIONS, vendorTypeLabel } from "@/constants/vendorTypes";
import SearchableSelect from "@/components/SearchableSelect";
import QboVendorSelect from "@/components/QboVendorSelect";
import VendorByEmailDialog from "@/components/VendorByEmailDialog";
import { useAuth } from "@/contexts/AuthContext";

const ROLES = CONTRACTOR_ROLES.map((r) => r.value);
const VENDOR_TYPES = [{ value: "", label: "Not set" }, ...VENDOR_TYPE_OPTIONS];

const TERMS_OPTIONS = [
  { value: "none", label: "None" },
  { value: "due_on_receipt", label: "Due on receipt" },
  { value: "net_15", label: "Net 15" },
  { value: "net_30", label: "Net 30" },
  { value: "net_45", label: "Net 45" },
  { value: "net_60", label: "Net 60" },
];

const emptyForm = {
  vendorId: "", restriction: "company", allowedRoles: [], allowedJobs: [],
  paymentTerms: "", onAccountAllowed: false, terms: "none", creditLimit: "",
  vendorType: "", accountNumber: "", notes: "",
};

const emptyLocalForm = { name: "", address: "", phone: "", contactName: "", email: "", notes: "", onAccountAllowed: false, terms: "none", creditLimit: "" };

export default function VendorNetwork() {
  const { user } = useAuth();
  const isAdmin = user?.role === "company_admin";
  const [qboConnected, setQboConnected] = useState(false);
  const [showByEmail, setShowByEmail] = useState(false);
  const [network, setNetwork] = useState([]);
  const [available, setAvailable] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [localVendors, setLocalVendors] = useState([]);
  const [showLocal, setShowLocal] = useState(false);
  const [editingLocal, setEditingLocal] = useState(null);
  const [localForm, setLocalForm] = useState(emptyLocalForm);

  const [showInvite, setShowInvite] = useState(false);
  const [invitingVendor, setInvitingVendor] = useState(null);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "" });
  const [inviting, setInviting] = useState(false);

  const load = () => {
    api.get("/contractor/vendor-network").then((r) => setNetwork(r.data));
    api.get("/contractor/vendors/available").then((r) => setAvailable(r.data));
  };
  // Deactivated vendors stay listed here (greyed) so they can be turned back on.
  const loadLocal = () => api.get("/contractor/local-vendors", { params: { include_inactive: true } }).then((r) => setLocalVendors(r.data));
  const [removeLocal, setRemoveLocal] = useState(null);

  const setNetworkActive = async (v, active) => {
    try {
      await api.patch(`/contractor/vendor-network/${v.id}/active`, { active });
      toast.success(active ? `${v.vendor_name} is back in your network` : `${v.vendor_name} removed from your network`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };
  const setLocalActive = async (v, active) => {
    try {
      await api.patch(`/contractor/local-vendors/${v.id}/active`, { active });
      toast.success(active ? `${v.name} reactivated` : `${v.name} deactivated`);
      loadLocal();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  // Poll the network list so invite status (pending → active once the
  // vendor sets their password) shows up on its own — no manual refresh
  // needed, matching the notification bell's existing polling convention.
  usePolling(load, 20000);

  useEffect(() => {
    loadLocal();
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
    if (isAdmin) api.get("/contractor/integrations/qbo/status").then((r) => setQboConnected(!!r.data.connected)).catch(() => {});
  }, [isAdmin]);

  // An invitation to the portal. A vendor already approved on ScapeSource is
  // invited directly; one that isn't (an out-of-network vendor, or someone
  // reached only by email) goes to ScapeSource staff for approval first and
  // is emailed once approved.
  const [linking, setLinking] = useState(null);
  const linkRegistered = async () => {
    const { data } = await api.post(`/contractor/vendor-network/${linking.vendor_id}/link-registered`, { vendor_id: linking.registered_twin.vendor_id });
    toast.success(`Linked to ${data.vendor_name} — ${data.records_moved} record(s) moved`);
    load();
  };

  const openInvite = (v) => {
    const queued = v.portal_status === "unregistered";
    setInvitingVendor({ kind: "network", key: v.vendor_id, label: v.vendor_name, portal_status: v.portal_status, queued });
    const pending = v.portal_status === "pending_invite" ? v.portal_contacts?.[0] : null;
    setInviteForm({ name: pending?.name || "", email: pending?.email || (queued ? v.contact_email || "" : "") });
    setShowInvite(true);
  };
  const openInviteLocal = (v) => {
    setInvitingVendor({ kind: "local", key: v.id, label: v.name, queued: true });
    setInviteForm({ name: v.onboarding?.contact_name || v.contact_name || "", email: v.onboarding?.email || v.email || "" });
    setShowInvite(true);
  };
  const sendInvite = async () => {
    const local = invitingVendor.kind === "local";
    if (!inviteForm.email.trim() || (!local && !inviteForm.name.trim())) return toast.error(local ? "Enter an email" : "Enter a name and email");
    setInviting(true);
    try {
      await api.post(local ? `/contractor/local-vendors/${invitingVendor.key}/invite` : `/contractor/vendors/${invitingVendor.key}/invite`,
        local ? { email: inviteForm.email.trim(), contact_name: inviteForm.name.trim() || null } : { name: inviteForm.name.trim(), email: inviteForm.email.trim() });
      toast.success(invitingVendor.queued
        ? `Sent to ScapeSource for approval — ${inviteForm.email.trim()} is emailed once it's approved`
        : `Invite sent to ${inviteForm.email.trim()}`);
      setShowInvite(false);
      load(); loadLocal();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.message || x.msg).join(" ") : d || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  const openAddLocal = () => { setEditingLocal(null); setLocalForm(emptyLocalForm); setShowLocal(true); };
  const openEditLocal = (v) => {
    setEditingLocal(v);
    setLocalForm({
      name: v.name, address: v.address || "", phone: v.phone || "", contactName: v.contact_name || "", email: v.email || "", notes: v.notes || "",
      onAccountAllowed: v.on_account_allowed || false, terms: v.terms || "none", creditLimit: v.credit_limit != null ? String(v.credit_limit) : "",
    });
    setShowLocal(true);
  };
  const saveLocal = async () => {
    if (!localForm.name.trim()) return toast.error("Enter a vendor name");
    const body = {
      name: localForm.name.trim(), address: localForm.address || null, phone: localForm.phone || null,
      contact_name: localForm.contactName || null, email: localForm.email.trim() || null, notes: localForm.notes || null,
      on_account_allowed: localForm.onAccountAllowed, terms: localForm.terms,
      credit_limit: localForm.creditLimit ? Number(localForm.creditLimit) : null,
    };
    try {
      if (editingLocal) {
        await api.patch(`/contractor/local-vendors/${editingLocal.id}`, body);
        toast.success("Out-of-network vendor updated");
      } else {
        await api.post("/contractor/local-vendors", body);
        toast.success("Out-of-network vendor added");
      }
      setShowLocal(false);
      loadLocal();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setShow(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      vendorId: v.vendor_id, restriction: v.restriction_level,
      allowedRoles: v.allowed_roles || [], allowedJobs: v.allowed_job_ids || [],
      paymentTerms: v.payment_terms || "", onAccountAllowed: v.on_account_allowed || false,
      terms: v.terms || "none", creditLimit: v.credit_limit != null ? String(v.credit_limit) : "",
      vendorType: v.vendor_type || "",
      accountNumber: v.account_number || "", notes: v.notes || "",
    });
    setShow(true);
  };

  const save = async () => {
    if (!form.vendorId) return toast.error("Select a vendor");
    const body = {
      vendor_id: form.vendorId, restriction_level: form.restriction,
      allowed_roles: form.allowedRoles, allowed_job_ids: form.allowedJobs,
      payment_terms: form.paymentTerms || null,
      on_account_allowed: form.onAccountAllowed, terms: form.terms,
      credit_limit: form.creditLimit ? Number(form.creditLimit) : null,
      vendor_type: form.vendorType || null,
      account_number: form.accountNumber || null, notes: form.notes || null,
    };
    try {
      if (editing) {
        await api.patch(`/contractor/vendor-network/${editing.id}`, body);
        toast.success("Vendor network entry updated");
      } else {
        await api.post("/contractor/vendor-network", body);
        toast.success("Vendor added to network");
      }
      setShow(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="vendor-network-page">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">Vendor Network</h1>
        <p className="text-slate-500 text-sm mt-1">In-network vendors your team can purchase from, and out-of-network vendors logged after the fact — like insurance. Select vendors from QuickBooks, or send a PO to anyone by email.</p>
      </div>

      <Tabs defaultValue="network" className="mt-6">
        <TabsList>
          <TabsTrigger value="network" data-testid="tab-in-network">In-Network ({network.length})</TabsTrigger>
          <TabsTrigger value="out-of-network" data-testid="tab-out-of-network">Out-of-Network ({localVendors.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="network" className="mt-5">
          <div className="flex items-center justify-end flex-wrap gap-2">
            {isAdmin && qboConnected && <QboVendorSelect onSaved={() => { load(); loadLocal(); }} />}
            <button onClick={() => setShowByEmail(true)} data-testid="add-vendor-by-email-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[13px] font-semibold">
              <AtSign size={14} /> Add by email
            </button>
            <CsvImportButton entity="vendors" label="Vendors" onImported={load} />
            <button onClick={openAdd} data-testid="add-vendor-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
              <Plus size={14} /> Add Vendor
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-5" data-testid="vendor-network-grid">
            {network.map((v) => (
          <div key={v.id} className="border border-slate-200 rounded-xl p-4 flex items-start gap-3" data-testid={`vendor-network-card-${v.vendor_id}`}>
            <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center text-sky-600 shrink-0"><Network size={16} /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-[14px]">{v.vendor_name}</div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={v.status} />
                  <button onClick={() => openEdit(v)} data-testid={`edit-vendor-network-${v.id}`} className="text-slate-400 hover:text-sky-600"><Pencil size={14} /></button>
                  <button onClick={() => setNetworkActive(v, v.status !== "active")} data-testid={`toggle-vendor-network-${v.id}`} title={v.status === "active" ? "Remove from network" : "Add back to network"} className={v.status === "active" ? "text-slate-400 hover:text-red-600" : "text-emerald-600 hover:text-emerald-700"}><Power size={14} /></button>
                </div>
              </div>
              <div className="text-[12px] text-slate-400 mt-0.5">{v.category}</div>
              <div className="text-[11.5px] text-slate-500 mt-1.5">Restriction: <span className="font-medium capitalize">{v.restriction_level}</span></div>
              <div className="text-[11.5px] text-slate-500 mt-1">
                Credit: {v.on_account_allowed
                  ? <span className="font-medium text-emerald-700">On account — {TERMS_OPTIONS.find((t) => t.value === v.terms)?.label || v.terms}{v.credit_limit ? ` (limit $${v.credit_limit.toLocaleString("en-US")})` : ""}</span>
                  : <span className="font-medium">Card only</span>}
              </div>
              {(v.vendor_type || v.payment_terms || v.account_number) && (
                <div className="text-[11.5px] text-slate-500 mt-1 flex flex-wrap gap-x-3">
                  {v.vendor_type && <span>Type: <span className="font-medium">{vendorTypeLabel(v.vendor_type)}</span></span>}
                  {v.payment_terms && <span>Legacy terms: <span className="font-medium">{v.payment_terms}</span></span>}
                  {v.account_number && <span>Acct #: <span className="font-medium">{v.account_number}</span></span>}
                </div>
              )}
              {v.notes && <div className="text-[11.5px] text-slate-400 mt-1">{v.notes}</div>}

              <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex items-center justify-between gap-2">
                {v.portal_status === "active" ? (
                  <div className="flex items-center gap-1.5 text-[11.5px] text-emerald-700 font-medium" data-testid={`vendor-portal-status-${v.vendor_id}`}>
                    <CheckCircle2 size={13} /> Portal access active
                    {v.portal_contacts?.[0]?.email && <span className="text-slate-400 font-normal">— {v.portal_contacts[0].email}</span>}
                  </div>
                ) : v.portal_status === "pending_invite" ? (
                  <div className="flex items-center gap-1.5 text-[11.5px] text-amber-600 font-medium" data-testid={`vendor-portal-status-${v.vendor_id}`}>
                    <Clock size={13} /> Invite sent — awaiting setup
                    {v.portal_contacts?.[0]?.email && <span className="text-slate-400 font-normal">— {v.portal_contacts[0].email}</span>}
                  </div>
                ) : v.portal_status === "awaiting_approval" ? (
                  <div className="flex items-center gap-1.5 text-[11.5px] text-amber-600 font-medium" data-testid={`vendor-portal-status-${v.vendor_id}`}>
                    <Clock size={13} /> Waiting for ScapeSource approval
                    {v.contact_email && <span className="text-slate-400 font-normal">— {v.contact_email}</span>}
                  </div>
                ) : v.portal_status === "unregistered" ? (
                  <div className="text-[11.5px] text-slate-500" data-testid={`vendor-portal-status-${v.vendor_id}`}>
                    Not on ScapeSource yet — POs are emailed{v.contact_email && <span className="text-slate-400"> to {v.contact_email}</span>}
                  </div>
                ) : v.registered_twin ? (
                  <div className="text-[11.5px] text-emerald-700" data-testid={`vendor-portal-status-${v.vendor_id}`}>
                    <span className="font-medium">{v.registered_twin.name}</span> is already on ScapeSource under a separate record
                  </div>
                ) : (
                  <div className="text-[11.5px] text-slate-400" data-testid={`vendor-portal-status-${v.vendor_id}`}>No portal access yet</div>
                )}
                {v.registered_twin && v.portal_status !== "active" && v.portal_status !== "awaiting_approval" && (
                  <button
                    onClick={() => setLinking(v)}
                    data-testid={`link-registered-vendor-${v.vendor_id}`}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-[11.5px] font-semibold shrink-0"
                  >
                    <CheckCircle2 size={13} /> Link to their account
                  </button>
                )}
                {!v.registered_twin && v.portal_status !== "active" && v.portal_status !== "awaiting_approval" && (
                  <button
                    onClick={() => openInvite(v)}
                    data-testid={`invite-vendor-contact-${v.vendor_id}`}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-sky-200 text-sky-600 hover:bg-sky-50 text-[11.5px] font-semibold shrink-0"
                  >
                    <UserPlus size={13} /> {v.portal_status === "pending_invite" ? "Resend" : "Invite to Portal"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
            {network.length === 0 && <div className="text-slate-400 text-sm col-span-2 py-10 text-center">No vendors in your network yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="out-of-network" className="mt-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-[12.5px] text-slate-500 max-w-lg">Vendors your team has bought from that aren't in the network — logged after the fact in the Expense Log. ScapeSource staff sees these across every contractor and may reach out to onboard the popular ones.</p>
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              {isAdmin && qboConnected && <QboVendorSelect onSaved={() => { load(); loadLocal(); }} />}
              <button onClick={openAddLocal} data-testid="add-local-vendor-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-semibold shrink-0">
                <Plus size={14} /> Add Out-of-Network Vendor
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-5" data-testid="local-vendor-grid">
            {localVendors.map((v) => (
              <div key={v.id} className={`border border-slate-200 rounded-xl p-4 flex items-start gap-3 ${v.active === false ? "opacity-60" : ""}`} data-testid={`local-vendor-card-${v.id}`}>
                <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center text-amber-700 shrink-0"><Building2 size={16} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-[14px]">{v.name}{v.active === false && <span className="ml-2 ss-pill ss-pill-gray">Inactive</span>}{v.source === "qbo" && <span className="ml-2 ss-pill ss-pill-green" title="Selected from your QuickBooks vendors">QuickBooks</span>}</div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => openEditLocal(v)} data-testid={`edit-local-vendor-${v.id}`} className="text-slate-400 hover:text-sky-600"><Pencil size={14} /></button>
                      <button onClick={() => setLocalActive(v, v.active === false)} data-testid={`toggle-local-vendor-${v.id}`} title={v.active === false ? "Reactivate" : "Deactivate"} className={v.active === false ? "text-emerald-600" : "text-slate-400 hover:text-amber-600"}><Power size={14} /></button>
                      <button onClick={() => setRemoveLocal(v)} data-testid={`delete-local-vendor-${v.id}`} title="Delete" className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  {v.address && <div className="text-[12px] text-slate-400 mt-0.5">{v.address}</div>}
                  {(v.contact_name || v.phone) && <div className="text-[11.5px] text-slate-500 mt-1">{v.contact_name}{v.contact_name && v.phone ? " · " : ""}{v.phone}</div>}
                  {v.email && <div className="text-[11.5px] text-slate-500 mt-0.5" data-testid={`local-vendor-email-${v.id}`}>{v.email}</div>}
                  <div className="text-[11.5px] text-slate-500 mt-1">
                    Credit: {v.on_account_allowed
                      ? <span className="font-medium text-emerald-700">On account — {TERMS_OPTIONS.find((t) => t.value === v.terms)?.label || v.terms}{v.credit_limit ? ` (limit $${v.credit_limit.toLocaleString("en-US")})` : ""}</span>
                      : <span className="font-medium">Card only</span>}
                  </div>
                  {v.notes && <div className="text-[11.5px] text-slate-400 mt-1">{v.notes}</div>}

                  <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex items-center justify-between gap-2" data-testid={`local-vendor-portal-${v.id}`}>
                    {v.onboarded_vendor_company_id ? (
                      <div className="flex items-center gap-1.5 text-[11.5px] text-emerald-700 font-medium"><CheckCircle2 size={13} /> Approved — now in your network</div>
                    ) : v.onboarding?.status === "pending" ? (
                      <div className="flex items-center gap-1.5 text-[11.5px] text-amber-600 font-medium"><Clock size={13} /> Waiting for ScapeSource approval<span className="text-slate-400 font-normal">— {v.onboarding.email}</span></div>
                    ) : v.onboarding?.status === "declined" ? (
                      <div className="text-[11.5px] text-red-600 font-medium">ScapeSource didn't approve the invitation</div>
                    ) : (
                      <div className="text-[11.5px] text-slate-400">Not on ScapeSource yet</div>
                    )}
                    {isAdmin && !v.onboarded_vendor_company_id && v.onboarding?.status !== "pending" && v.active !== false && (
                      <button onClick={() => openInviteLocal(v)} data-testid={`invite-local-vendor-${v.id}`}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-sky-200 text-sky-600 hover:bg-sky-50 text-[11.5px] font-semibold shrink-0">
                        <UserPlus size={13} /> {v.onboarding?.status === "declined" ? "Invite again" : "Invite to Portal"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {localVendors.length === 0 && <div className="text-slate-400 text-sm col-span-2 py-10 text-center">No out-of-network vendors added yet.</div>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent data-testid="vendor-network-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit Vendor Network Entry" : "Add Vendor to Network"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {editing ? (
              <div>
                <label className="text-[13px] font-medium">Vendor</label>
                <div className="mt-1.5 h-10 px-3 rounded-lg border border-slate-100 bg-slate-50 text-sm flex items-center text-slate-600">{editing.vendor_name}</div>
              </div>
            ) : (
              <div>
                <label className="text-[13px] font-medium">Vendor *</label>
                <SearchableSelect value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: e.target.value })} data-testid="available-vendor-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                  <option value="">Select approved vendor…</option>
                  {available.map((v) => <option key={v.id} value={v.id}>{v.name} — {v.category}</option>)}
                </SearchableSelect>
              </div>
            )}
            <div>
              <label className="text-[13px] font-medium">Restriction Level</label>
              <SearchableSelect value={form.restriction} onChange={(e) => setForm({ ...form, restriction: e.target.value })} data-testid="restriction-level-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                <option value="company">Entire company</option>
                <option value="role">Specific roles</option>
                <option value="job">Specific jobs</option>
              </SearchableSelect>
            </div>
            {form.restriction === "role" && (
              <div>
                <label className="text-[13px] font-medium">Allowed Roles</label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {ROLES.map((r) => (
                    <label key={r} className="flex items-center gap-1.5 text-[12.5px] border border-slate-200 rounded-full px-2.5 py-1">
                      <input type="checkbox" checked={form.allowedRoles.includes(r)} onChange={(e) => setForm({ ...form, allowedRoles: e.target.checked ? [...form.allowedRoles, r] : form.allowedRoles.filter((x) => x !== r) })} />
                      {roleLabel(r)}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {form.restriction === "job" && (
              <div>
                <label className="text-[13px] font-medium">Allowed Jobs</label>
                <div className="mt-1.5 flex flex-col gap-1.5 max-h-32 overflow-y-auto ss-scrollbar">
                  {jobs.map((j) => (
                    <label key={j.id} className="flex items-center gap-1.5 text-[12.5px]">
                      <input type="checkbox" checked={form.allowedJobs.includes(j.id)} onChange={(e) => setForm({ ...form, allowedJobs: e.target.checked ? [...form.allowedJobs, j.id] : form.allowedJobs.filter((x) => x !== j.id) })} />
                      {j.code} — {j.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="text-[13px] font-medium">Vendor Type</label>
              <SearchableSelect value={form.vendorType} onChange={(e) => setForm({ ...form, vendorType: e.target.value })} data-testid="vendor-type-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                {VENDOR_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </SearchableSelect>
            </div>

            {/* Package 22 — credit terms for THIS contractor-vendor
                relationship; drives the card-vs-on-account choice at
                post-approval checkout. */}
            <div className="border border-slate-200 rounded-lg p-3 space-y-2.5">
              <label className="flex items-center gap-2 text-[13px] font-medium">
                <input
                  type="checkbox" checked={form.onAccountAllowed}
                  onChange={(e) => setForm({ ...form, onAccountAllowed: e.target.checked, terms: e.target.checked && form.terms === "none" ? "net_30" : form.terms })}
                  data-testid="on-account-allowed-checkbox"
                />
                This vendor allows on-account credit for this company
              </label>
              {form.onAccountAllowed && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[12px] text-slate-500">Terms</label>
                    <SearchableSelect value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} data-testid="credit-terms-select" className="mt-1 w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]">
                      {TERMS_OPTIONS.filter((t) => t.value !== "none").map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </SearchableSelect>
                  </div>
                  <div>
                    <label className="text-[12px] text-slate-500">Credit limit (optional)</label>
                    <input type="number" min="0" value={form.creditLimit} onChange={(e) => setForm({ ...form, creditLimit: e.target.value })} placeholder="No limit" data-testid="credit-limit-input" className="mt-1 w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]" />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="text-[13px] font-medium">Legacy payment terms note</label>
              <input value={form.paymentTerms} onChange={(e) => setForm({ ...form, paymentTerms: e.target.value })} placeholder="Free-text, e.g. net_30 — superseded by the credit terms above" data-testid="payment-terms-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <input value={form.accountNumber} onChange={(e) => setForm({ ...form, accountNumber: e.target.value })} placeholder="Account number (optional)" data-testid="account-number-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" data-testid="vendor-notes-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" rows={2} />
            <button onClick={save} data-testid="confirm-vendor-network-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold mt-2">{editing ? "Save Changes" : "Add to Network"}</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showLocal} onOpenChange={setShowLocal}>
        <DialogContent data-testid="local-vendor-modal">
          <DialogHeader><DialogTitle>{editingLocal ? "Edit Out-of-Network Vendor" : "Add Out-of-Network Vendor"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[13px] font-medium">Vendor Name *</label>
              <input value={localForm.name} onChange={(e) => setLocalForm({ ...localForm, name: e.target.value })} placeholder="e.g. Joe's Mulch Yard" data-testid="local-vendor-name-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <input value={localForm.address} onChange={(e) => setLocalForm({ ...localForm, address: e.target.value })} placeholder="Address (optional)" data-testid="local-vendor-address-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input value={localForm.contactName} onChange={(e) => setLocalForm({ ...localForm, contactName: e.target.value })} placeholder="Contact name" data-testid="local-vendor-contact-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <input value={localForm.phone} onChange={(e) => setLocalForm({ ...localForm, phone: e.target.value })} placeholder="Phone" data-testid="local-vendor-phone-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <input type="email" value={localForm.email} onChange={(e) => setLocalForm({ ...localForm, email: e.target.value })} placeholder="Email (needed to invite them to the portal)" data-testid="local-vendor-email-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />

            <div className="border border-slate-200 rounded-lg p-3 space-y-2.5">
              <label className="flex items-center gap-2 text-[13px] font-medium">
                <input
                  type="checkbox" checked={localForm.onAccountAllowed}
                  onChange={(e) => setLocalForm({ ...localForm, onAccountAllowed: e.target.checked, terms: e.target.checked && localForm.terms === "none" ? "net_30" : localForm.terms })}
                  data-testid="local-vendor-on-account-checkbox"
                />
                This vendor allows on-account credit (walk-in account)
              </label>
              {localForm.onAccountAllowed && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[12px] text-slate-500">Terms</label>
                    <SearchableSelect value={localForm.terms} onChange={(e) => setLocalForm({ ...localForm, terms: e.target.value })} data-testid="local-vendor-terms-select" className="mt-1 w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]">
                      {TERMS_OPTIONS.filter((t) => t.value !== "none").map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </SearchableSelect>
                  </div>
                  <div>
                    <label className="text-[12px] text-slate-500">Credit limit (optional)</label>
                    <input type="number" min="0" value={localForm.creditLimit} onChange={(e) => setLocalForm({ ...localForm, creditLimit: e.target.value })} placeholder="No limit" data-testid="local-vendor-credit-limit-input" className="mt-1 w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[12.5px]" />
                  </div>
                </div>
              )}
            </div>

            <textarea value={localForm.notes} onChange={(e) => setLocalForm({ ...localForm, notes: e.target.value })} placeholder="Notes (optional)" data-testid="local-vendor-notes-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" rows={2} />
            <button onClick={saveLocal} data-testid="confirm-local-vendor-button" className="w-full h-10 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-semibold mt-2">{editingLocal ? "Save Changes" : "Add Vendor"}</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent data-testid="vendor-invite-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={16} className="text-sky-500" /> Invite {invitingVendor?.label} to the Vendor Portal</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {invitingVendor?.queued ? (
              <p className="text-[12.5px] text-slate-500" data-testid="invite-queue-note">
                ScapeSource staff review every new vendor first. Once they approve, this email gets the invitation to set up their portal and
                {" "}{invitingVendor?.label} joins your network. Nothing is sent to the vendor until then.
              </p>
            ) : (
              <p className="text-[12.5px] text-slate-500">
                They'll get an email with a link to set their own password. Once activated, they can log in, view POs
                you send them, acknowledge orders, update delivery status, and submit invoices — directly, without you
                relaying it for them.
              </p>
            )}
            <div>
              <label className="text-[13px] font-medium">Contact name {invitingVendor?.kind === "local" ? "" : "*"}</label>
              <input
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="e.g. Renata Cruz"
                data-testid="vendor-invite-name-input"
                className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
              />
            </div>
            <div>
              <label className="text-[13px] font-medium">Email *</label>
              <input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="vendor@example.com"
                data-testid="vendor-invite-email-input"
                className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
              />
            </div>
            <button
              onClick={sendInvite}
              disabled={inviting}
              data-testid="confirm-vendor-invite-button"
              className="w-full h-10 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold mt-2 disabled:opacity-60"
            >
              {inviting ? "Sending…" : invitingVendor?.queued ? "Send for approval" : invitingVendor?.portal_status === "pending_invite" ? "Resend Invite" : "Send Invite"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <VendorByEmailDialog open={showByEmail} onOpenChange={setShowByEmail} onCreated={load} />
      <ConfirmAction open={!!linking} onOpenChange={(o) => !o && setLinking(null)} danger={false} confirmLabel="Link vendor"
        title={`Link ${linking?.vendor_name} to ${linking?.registered_twin?.name}?`}
        description="This vendor already signed up on ScapeSource. Linking moves your purchase orders, invoices and RFQs for it onto their real account, so they see them in their portal. Your history is kept."
        onConfirm={linkRegistered} testId="link-registered-dialog" />
      <ConfirmAction open={!!removeLocal} onOpenChange={(o) => !o && setRemoveLocal(null)} title={`Delete ${removeLocal?.name}?`}
        description="Only a vendor no expense uses can be deleted. One with history can be deactivated instead." confirmLabel="Delete" testId="delete-local-vendor-dialog"
        onConfirm={async () => { await api.delete(`/contractor/local-vendors/${removeLocal.id}`); toast.success(`${removeLocal.name} deleted`); loadLocal(); }} />
    </div>
  );
}
